import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useConfig } from '../../context/ConfigContext';
import { useLanguage } from '../../context/LanguageContext';
import { getMobilePlatform, isStandalone, isInAppWebView } from '../../utils/platform';
import { getItem, setItem, STORAGE_KEYS } from '../../utils/storage';

/**
 * Slim top bar shown to mobile web visitors, inviting them to open the native
 * Foubert app (or download it from the store when it isn't installed yet).
 *
 * Everything is driven by config.json:
 *   - shared.app.{enabled, androidUrl, iosUrl, deepLink}
 *   - translations.<lang>.appBanner.{title, subtitle, cta, dismiss}
 *
 * The bar hides itself automatically when:
 *   - the app already runs as an installed (standalone) app,
 *   - the site is embedded (iframe mode → header is hidden),
 *   - the visitor is on desktop, or the user dismissed it earlier.
 */
function AppInstallBanner() {
    const { shared, getTranslation, loading } = useConfig();
    const { language } = useLanguage();

    // hideHeader is set in iframe mode (?iframe=no) — mirror the header's visibility.
    const hideHeader = useSelector(state => state.overlay.hideHeader || false);

    // QA override: ?appbanner=android|ios|1 forces the (mobile-only) banner to
    // show so it can be verified on desktop. Ignored when the value is unknown.
    const [override] = useState(() => {
        try {
            const value = new URLSearchParams(window.location.search).get('appbanner');
            if (value === 'ios' || value === 'android') return value;
            if (value === '1' || value === 'test') return 'force';
            return null;
        } catch {
            return null;
        }
    });

    const detected = getMobilePlatform();
    const platform =
        override === 'ios' || override === 'android'
            ? override
            : detected || (override === 'force' ? 'android' : null);

    // Treat "already installed" as: standalone PWA OR running inside the
    // native wrapper app's web view (don't invite people already in the app).
    const [standalone] = useState(() => isStandalone() || isInAppWebView());
    const [dismissed, setDismissed] = useState(
        () => getItem(STORAGE_KEYS.APP_BANNER_DISMISSED) === '1'
    );

    const appConfig = shared?.app || {};
    const translation = getTranslation(language) || {};
    const text = translation.appBanner || {};

    const storeUrl = platform === 'ios' ? appConfig.iosUrl : appConfig.androidUrl;

    const handleDismiss = useCallback(() => {
        setItem(STORAGE_KEYS.APP_BANNER_DISMISSED, '1');
        setDismissed(true);
    }, []);

    const handleOpen = useCallback(() => {
        if (!storeUrl && !appConfig.deepLink) return;

        // If a deep link is configured, try to open the installed app first and
        // fall back to the store when nothing handles it (i.e. app not installed).
        if (appConfig.deepLink && storeUrl) {
            const fallback = window.setTimeout(() => {
                window.location.href = storeUrl;
            }, 1500);

            // If the app opens, the page is backgrounded — cancel the store fallback.
            const clear = () => window.clearTimeout(fallback);
            window.addEventListener('pagehide', clear, { once: true });
            window.addEventListener('visibilitychange', clear, { once: true });

            window.location.href = appConfig.deepLink;
            return;
        }

        // No deep link → straight to the store (download).
        window.location.href = appConfig.deepLink || storeUrl;
    }, [appConfig.deepLink, storeUrl]);

    const shouldShow =
        !loading &&
        appConfig.enabled !== false &&
        (override ? true : !dismissed) &&
        !hideHeader &&
        (override ? true : !standalone) &&
        !!platform &&
        (!!storeUrl || !!appConfig.deepLink);

    if (!shouldShow) return null;

    return (
        <div className="app-install-banner" role="region" aria-label={text.title || 'App'}>
            <button
                type="button"
                className="app-install-banner__close"
                onClick={handleDismiss}
                aria-label={text.dismiss || 'Sluiten'}
            >
                &times;
            </button>
            <img
                className="app-install-banner__icon"
                src={(shared?.images?.logo) || '/images/logo.png'}
                alt=""
                aria-hidden="true"
            />
            <div className="app-install-banner__text">
                <span className="app-install-banner__title">{text.title || 'Foubert app'}</span>
                {text.subtitle ? (
                    <span className="app-install-banner__subtitle">{text.subtitle}</span>
                ) : null}
            </div>
            <button
                type="button"
                className="app-install-banner__cta"
                onClick={handleOpen}
            >
                {text.cta || 'Open'}
            </button>
        </div>
    );
}

export default AppInstallBanner;
